import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, extractErr } from "./api";
import { useAuth } from "./auth";
import { toast } from "sonner";

const LocationCtx = createContext(null);

export function LocationProvider({ children }) {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem("operon.activeLocationId") || "all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/locations");
      const list = data.locations || [];
      setLocations(list);
      // Validate active id; fall back to 'all'
      const stored = localStorage.getItem("operon.activeLocationId");
      if (stored && stored !== "all" && !list.find((l) => l.id === stored)) {
        setActiveId("all");
        localStorage.setItem("operon.activeLocationId", "all");
      }
    } catch (e) {
      // soft fail; locations endpoint should not gate the rest of the app
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const setLocation = useCallback((id) => {
    setActiveId(id || "all");
    localStorage.setItem("operon.activeLocationId", id || "all");
  }, []);

  const refresh = load;

  // Provide a helper to derive a location_id query param. Returns {} if 'all'.
  const locationParams = useMemo(() => {
    if (!activeId || activeId === "all") return {};
    return { location_id: activeId };
  }, [activeId]);

  const activeLocation = useMemo(
    () => (activeId === "all" ? null : locations.find((l) => l.id === activeId) || null),
    [activeId, locations]
  );

  const create = async (payload) => {
    try {
      const { data } = await api.post("/locations", payload);
      toast.success(`Location “${data.name}” created`);
      await load();
      return data;
    } catch (e) {
      toast.error(extractErr(e, "Failed to create location"));
      throw e;
    }
  };

  const update = async (id, payload) => {
    try {
      const { data } = await api.put(`/locations/${id}`, payload);
      toast.success("Location updated");
      await load();
      return data;
    } catch (e) {
      toast.error(extractErr(e, "Failed to update location"));
      throw e;
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Location removed");
      if (activeId === id) setLocation("all");
      await load();
    } catch (e) {
      toast.error(extractErr(e, "Failed to remove location"));
      throw e;
    }
  };

  return (
    <LocationCtx.Provider value={{
      locations, activeId, activeLocation, loading,
      setLocation, refresh, create, update, remove, locationParams,
    }}>
      {children}
    </LocationCtx.Provider>
  );
}

export const useLocations = () => useContext(LocationCtx);
